import React, { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Skeleton } from '../components/ui/skeleton';
import { matchesAPI, playersAPI } from '../lib/api';
import { formatDate, formatDuration, getStatusColor } from '../lib/utils';
import { Activity, Clock, Target, Users, ChevronRight } from 'lucide-react';

export default function Matches() {
  const [searchParams] = useSearchParams();
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const initialStatus = searchParams.get('status') || 'all';
  const [loading, setLoading] = useState(true);
  const [matches, setMatches] = useState([]);
  const [players, setPlayers] = useState({});
  const [activeTab, setActiveTab] = useState(initialStatus);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [matchesRes, playersRes] = await Promise.all([
          matchesAPI.getAll(),
          playersAPI.getAll(),
        ]);
        
        setMatches(matchesRes.data || []);
        
        // Create players lookup
        const playersLookup = {};
        (playersRes.data || []).forEach((p) => {
          playersLookup[p.user_id] = p;
        });
        setPlayers(playersLookup);
      } catch (error) {
        console.error('Error fetching matches:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const filteredMatches = matches.filter((m) => {
    if (activeTab === 'all') return true;
    return m.status === activeTab;
  });

  const getPlayerName = (match, playerId, field) => {
    // For guest matches, use the stored names
    if (match.is_guest) {
      if (field === 'player1') return match.player1_name || 'Player 1';
      if (field === 'player2') return match.player2_name || 'Player 2';
      if (field === 'player1_partner') return match.player1_partner_name || '';
      if (field === 'player2_partner') return match.player2_partner_name || '';
    }
    return players[playerId]?.name || 'Unknown Player';
  };

  if (loading) {
    return (
      <Layout>
        <div className="space-y-6">
          <Skeleton className="h-8 w-48" />
          <Skeleton className="h-12 w-full" />
          <div className="space-y-4">
            {[1, 2, 3].map((i) => (
              <Skeleton key={i} className="h-24" />
            ))}
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6" data-testid="matches-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="font-heading text-3xl font-bold uppercase tracking-tight">Matches</h1>
            <p className="text-muted-foreground mt-1">View and manage badminton matches</p>
          </div>
          <div className="flex gap-2">
            <Link to="/guest">
              <Button variant="outline" data-testid="guest-match-btn">
                <Target className="mr-2 h-4 w-4" />
                Quick Match
              </Button>
            </Link>
            <Button
                className="bg-secondary hover:bg-secondary/90"
                data-testid="new-match-btn"
                onClick={(e) => {
                  if (!isAuthenticated) {
                    e.preventDefault();
                    navigate('/login');
                  } else {
                    navigate('/matches/new');
                  }
                }}
              >
                <Target className="mr-2 h-4 w-4" />
                Create Match
              </Button>
          </div>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-4">
            <TabsTrigger value="all" data-testid="tab-all">All</TabsTrigger>
            <TabsTrigger value="live" data-testid="tab-live">
              <span className="flex items-center gap-2">
                <span className="h-2 w-2 rounded-full bg-red-500 live-indicator"></span>
                Live
              </span>
            </TabsTrigger>
            <TabsTrigger value="scheduled" data-testid="tab-scheduled">Scheduled</TabsTrigger>
            <TabsTrigger value="completed" data-testid="tab-completed">Completed</TabsTrigger>
          </TabsList>

          <TabsContent value={activeTab} className="mt-6">
            {filteredMatches.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <Activity className="h-12 w-12 mx-auto mb-4 text-muted-foreground opacity-50" />
                  <p className="text-muted-foreground">
                    No {activeTab !== 'all' ? activeTab : ''} matches found
                  </p>
                  <Button variant="outline" onClick={() => {
                      if (!isAuthenticated) { navigate('/login'); } else { navigate('/matches/new'); }
                    }}>Create New Match</Button>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {filteredMatches.map((match) => (
                  <Link key={match.match_id} to={match.is_guest ? `/guest/${match.match_id}` : `/matches/${match.match_id}`}>
                    <Card className="hover:shadow-md hover:border-primary/50 transition-all cursor-pointer">
                      <CardContent className="p-6">
                        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                          {/* Match Info */}
                          <div className="flex items-center gap-4">
                            <Badge className={getStatusColor(match.status)}>
                              {match.status === 'live' && (
                                <span className="h-2 w-2 rounded-full bg-white mr-2 live-indicator"></span>
                              )}
                              {match.status}
                            </Badge>
                            <div>
                              <p className="font-heading font-bold text-lg">
                                {match.match_type === 'singles' ? 'Singles' : 'Doubles'} Match
                                {match.is_guest && <span className="text-xs text-muted-foreground ml-2 font-normal">(Quick Match)</span>}
                              </p>
                              <p className="text-sm text-muted-foreground">
                                {formatDate(match.created_at)}
                              </p>
                            </div>
                          </div>

                          {/* Players */}
                          <div className="flex items-center gap-4">
                            <div className="text-center">
                              <p className="font-bold">{getPlayerName(match, match.player1_id, 'player1')}</p>
                              {(match.player1_partner_id || match.player1_partner_name) && (
                                <p className="font-bold text-sm">
                                  & {getPlayerName(match, match.player1_partner_id, 'player1_partner')}
                                </p>
                              )}
                            </div>
                            <span className="text-muted-foreground font-bold">vs</span>
                            <div className="text-center">
                              <p className="font-bold">{getPlayerName(match, match.player2_id, 'player2')}</p>
                              {(match.player2_partner_id || match.player2_partner_name) && (
                                <p className="font-bold text-sm">
                                  & {getPlayerName(match, match.player2_partner_id, 'player2_partner')}
                                </p>
                              )}
                            </div>
                          </div>

                          {/* Score */}
                          <div className="text-right">
                            <div className="font-mono font-bold text-xl">
                              {match.games?.map((g, i) => (
                                <span key={i} className="mx-1">
                                  {g.player1_score}-{g.player2_score}
                                </span>
                              ))}
                            </div>
                            {match.duration_seconds > 0 && (
                              <p className="text-sm text-muted-foreground flex items-center justify-end gap-1">
                                <Clock className="h-3 w-3" />
                                {formatDuration(match.duration_seconds)}
                              </p>
                            )}
                          </div>

                          <ChevronRight className="h-5 w-5 text-muted-foreground hidden md:block" />
                        </div>
                      </CardContent>
                    </Card>
                  </Link>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
}
